import { Component } from 'react';
import PropTypes from 'prop-types';
import { isPlainObject, isEmpty } from 'lodash';
import moment from 'moment';
import { isCurrentYear } from 'lib/date';
import { Button, Option } from '@schibsted-svp/react-ui';
import config from 'config';
import { MdClose } from 'react-icons/md';
import { getFormattedTime } from 'lib/time';
import { isCustomField, getCustomFieldLabel } from 'components/core/FiltersDialog/DynamicFilters/helpers';
import classnames from 'classnames/bind';

import css from './AppliedFilters.module.scss';

const cln = classnames.bind(css);

const MAX_VISIBLE_EXCLUDED_ASSETS_IDS = 20;

const getValue = (filter) => (isPlainObject(filter) ? filter.value : filter);

const formatLabel = (filterType, label, categoryCustomDateFields, categoryCustomBooleanFields) => {
    if (isCustomField(categoryCustomDateFields, filterType)) {
        const date = moment.unix(label);
        return `${getCustomFieldLabel(categoryCustomDateFields, filterType)} ${date.format(
            `Do MMM ${isCurrentYear(date) ? '' : 'YYYY'}`
        )}`;
    }
    if (isCustomField(categoryCustomBooleanFields, filterType)) {
        return getCustomFieldLabel(categoryCustomBooleanFields, filterType);
    }
    switch (filterType) {
        case 'before':
        case 'after': {
            const date = moment.unix(label);
            return `${filterType} ${date.format(`Do MMM ${isCurrentYear(date) ? '' : 'YYYY'}`)}`;
        }
        case 'moods':
            return config.asset.mood.values[label].label || label;
        case 'isPodcast':
            return 'Podcast';
        case 'isGeoblocked':
            return 'Geoblocked';
        case 'isDrmProtected':
            return 'DRM';
        case 'min':
            return `duration >= ${getFormattedTime(label * 1000)}`;
        case 'max':
            return `duration <= ${getFormattedTime(label * 1000)}`;
        case 'showHidden':
            return 'Show hidden assets';
        case 'excludeArchivedCategories':
            return 'Exclude archived categories';
        default:
            return label;
    }
};

class AppliedFilters extends Component {
    static propTypes = {
        onFiltersClear: PropTypes.func.isRequired,
        query: PropTypes.string,
        filters: PropTypes.object,
        onQueryDeletion: PropTypes.func.isRequired,
        onFilterDeletion: PropTypes.func.isRequired,
        categoryCustomDateFields: PropTypes.arrayOf(PropTypes.string),
        categoryCustomBooleanFields: PropTypes.arrayOf(PropTypes.bool),
        clearButtonLabel: PropTypes.string,
        forceShow: PropTypes.bool,
        buttonSize: PropTypes.string,
        className: PropTypes.string,
        nowrap: PropTypes.bool,
    };

    static defaultProps = {
        query: undefined,
        filters: {},
        categoryCustomDateFields: [],
        categoryCustomBooleanFields: [],
        clearButtonLabel: undefined,
        forceShow: false,
        buttonSize: 'standard',
        className: undefined,
        nowrap: false,
    };

    state = {
        showAll: false,
    };

    handleFilterRemove = (type, filter) => () => {
        const { onFilterDeletion } = this.props;
        onFilterDeletion({ filter: type, value: getValue(filter) });
    };

    render() {
        const {
            onFiltersClear,
            query,
            filters,
            onQueryDeletion,
            categoryCustomDateFields,
            categoryCustomBooleanFields,
            clearButtonLabel,
            forceShow,
            buttonSize,
            className,
            nowrap,
        } = this.props;

        if (isEmpty(filters) && !query && !forceShow) {
            return null;
        }

        const filtersValues = Object.entries(filters)
            .filter(([, filterValues]) => filterValues !== false)
            .map(([filterType, filterValues]) => [filterType, [].concat(filterValues)]);

        const { filtersValuesWithoutExcludedAssets, filtersValuesExcludedAssets } = filtersValues.reduce(
            (acc, [filterType, filterValues]) => {
                if (filterType === 'excludedAssets') {
                    acc.filtersValuesExcludedAssets = filterValues;
                } else {
                    acc.filtersValuesWithoutExcludedAssets.push([filterType, filterValues]);
                }
                return acc;
            },
            { filtersValuesWithoutExcludedAssets: [], filtersValuesExcludedAssets: [] }
        );

        const strikeFilters = ['excludedCategory', 'excludedTags', 'excludedAssets'];

        const visibleFiltersValuesExcludedAssets = this.state.showAll
            ? filtersValuesExcludedAssets
            : filtersValuesExcludedAssets.slice(0, MAX_VISIBLE_EXCLUDED_ASSETS_IDS);

        const remainingExcludedAssetsCount =
            filtersValuesExcludedAssets.length - visibleFiltersValuesExcludedAssets.length;
        return (
            <div className={className}>
                <section className={cln(css.container, { [css.containerNowrap]: nowrap })}>
                    <Button type="button" variant="standard" size={buttonSize} onClick={onFiltersClear}>
                        {clearButtonLabel || 'Clear'}
                    </Button>
                    {query && (
                        <div className={css.searchLabel}>
                            <div>Showing results for:</div>
                            <Option onClick={onQueryDeletion}>
                                <Option.Icon>
                                    <MdClose size={20} />
                                </Option.Icon>
                                <Option.Label>{query}</Option.Label>
                            </Option>
                        </div>
                    )}
                    {!isEmpty(filtersValuesWithoutExcludedAssets) && (
                        <div className={css.filtersContainer}>
                            <div>Filtered by:</div>
                            <div className={cln(css.filters, { [css.filtersNowrap]: nowrap })}>
                                {filtersValuesWithoutExcludedAssets.map(([filterType, filterValues]) =>
                                    filterValues.map((filter) => (
                                        <Option
                                            key={getValue(filter)}
                                            className={cln({
                                                strikethroughText: strikeFilters.includes(filterType),
                                            })}
                                            variant="option"
                                            onClick={this.handleFilterRemove(filterType, filter)}
                                        >
                                            <Option.Icon>
                                                <MdClose size={20} />
                                            </Option.Icon>
                                            <Option.Label>
                                                {formatLabel(
                                                    filterType,
                                                    isPlainObject(filter) ? filter.label : filter,
                                                    categoryCustomDateFields,
                                                    categoryCustomBooleanFields
                                                )}
                                            </Option.Label>
                                        </Option>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                    {!isEmpty(filtersValuesExcludedAssets) && (
                        <div className={css.excludedAssetsContainer}>
                            <div>Excluded assets:</div>
                            <div className={css.excludedAssetsItems}>
                                {visibleFiltersValuesExcludedAssets.map((filter) => (
                                    <Option
                                        key={getValue(filter)}
                                        className={css.strikethroughText}
                                        variant="option"
                                        onClick={this.handleFilterRemove('excludedAssets', filter)}
                                    >
                                        <Option.Icon>
                                            <MdClose size={20} />
                                        </Option.Icon>
                                        <Option.Label>{filter}</Option.Label>
                                    </Option>
                                ))}
                            </div>
                            {filtersValuesExcludedAssets.length > MAX_VISIBLE_EXCLUDED_ASSETS_IDS ? (
                                <Button
                                    type="button"
                                    onClick={() => {
                                        this.setState((prevState) => ({ showAll: !prevState.showAll }));
                                    }}
                                    size="small"
                                    variant="standard"
                                    className={cln('toggleButton', {
                                        'toggleButton--expanded': this.state.showAll,
                                    })}
                                >
                                    {this.state.showAll ? 'Show less' : `Load more (${remainingExcludedAssetsCount})`}
                                </Button>
                            ) : null}
                        </div>
                    )}
                </section>
            </div>
        );
    }
}

export default AppliedFilters;
